// src/utility/reportUtils.ts

import { httpService } from '../utility/httpService';
import { API_BASE } from '../config/env';
import { httpConstants } from '../constants';
import { FDToast } from '../constants/functionalDesign';
import Toast, { ToastTypeEnum } from '../common/components/ShowToast';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
// @ts-ignore
import ImageModule from 'docxtemplater-image-module-free';

import BpmnJS from 'bpmn-js';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min?worker';

pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();
delete (pdfjsLib.GlobalWorkerOptions as any).workerSrc;

/**
 * Generate a Word document Blob from a DOCX template and data map (including images).
 */
export const generateWordBlob = async (
  docxTemplateUrl: string,
  docxData: Record<string, unknown>
): Promise<Blob> => {
  // 1) Load template
  const response = await fetch(docxTemplateUrl);
  if (!response.ok) {
    Toast({ message: FDToast.FILE_LOAD_ERROR, type: ToastTypeEnum.Error });
    throw new Error(`Failed to load template: ${docxTemplateUrl}`);
  }
  const templateArrayBuffer = await response.arrayBuffer();

  // 2) Create PizZip & configure ImageModule
  const zip = new PizZip(templateArrayBuffer);

  const base64PrefixRegex = /^data:image\/\w+;base64,/;

  const imageModule = new ImageModule({
    getImage: (tagValue: string) => {
      let b64 = (tagValue as string).trim();
      if (base64PrefixRegex.test(b64)) {
        b64 = b64.replace(base64PrefixRegex, '');
      }

      const binary = atob(b64);
      const len = binary.length;
      const buffer = new ArrayBuffer(len);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < len; i++) {
        view[i] = binary.charCodeAt(i);
      }
      return buffer;
    },
    getImageMimeType: () => 'image/png',
    getSize: () => [600, 300],
  });


  // 3) Create Docxtemplater instance with image module
  const doc = new Docxtemplater(zip, {
    modules: [imageModule],
    delimiters: { start: '[[' as const, end: ']]' as const },
    paragraphLoop: true,
    linebreaks: true,
  });

  console.log('DocxTemplater data:', docxData, doc);

  doc.render(docxData);

  // 6) Export as Blob
  const outBuffer = doc.getZip().generate({ type: 'arraybuffer' });
  return new Blob([outBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

/**
 * Generate a PDF (base64) from a DOCX template + data
 */
export const generatePdfBased64 = async (
  docxTemplateUrl: string,
  docxData: Record<string, unknown>
): Promise<string | undefined> => {
  try {
    const wordBlob = await generateWordBlob(docxTemplateUrl, docxData);
    const formData = new FormData();
    formData.append('file', wordBlob, 'generated.docx');

    const res = await httpService<{ pdf_base64: string }>(
      httpConstants.METHOD_TYPE.POST,
      `${API_BASE}/api/v1/word-to-pdf/`,
      formData
    );
    return res?.pdf_base64;
  } catch (error) {
    console.error(`${FDToast.PDF_GENERATION_ERROR}: `, error);
    Toast({ message: FDToast.PDF_GENERATION_ERROR, type: ToastTypeEnum.Error });
    return undefined;
  }
};

/**
 * Convert a Base64 string to Blob
 */
export const convertBase64ToBlob = (base64: string, type: string): Blob => {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type });
};

/**
 * Save a generated PDF base64 string as a downloadable file
 */
export const saveGeneratedDocument = async (
  pdfBase64String: string | undefined,
  fileName: string
) => {
  if (!pdfBase64String) {
    Toast({ message: FDToast.NO_DOC_TO_SAVE, type: ToastTypeEnum.Error });
    return;
  }
  const fileBlob = convertBase64ToBlob(pdfBase64String, 'application/pdf');
  const url = window.URL.createObjectURL(fileBlob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const convertBpmnXmlToBase64 = async (
  xml: string,
  scaleFactor: number = 2
): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    // Base dimensions for the container
    const baseWidth = 1400;
    const baseHeight = 800;

    // Create container at base size for proper BPMN rendering
    const container = document.createElement('div');
    container.style.width = `${baseWidth}px`;
    container.style.height = `${baseHeight}px`;
    container.style.position = 'absolute';
    container.style.left = '-99999px';
    container.style.top = '-99999px';
    container.style.backgroundColor = 'white';
    container.style.overflow = 'hidden';
    container.style.transform = 'translateZ(0)';
    document.body.appendChild(container);

    let bpmnViewer: any;

    try {
      bpmnViewer = new BpmnJS({
        container,
        keyboard: { bindTo: null },
        additionalModules: [],
      });

      await bpmnViewer.importXML(xml);
      const canvas = bpmnViewer.get('canvas');

      // Fit diagram to viewport with padding
      canvas.zoom('fit-viewport', 'auto');

      // Wait for rendering
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const svgElement = container.querySelector('svg');
      if (!svgElement) {
        throw new Error('SVG element not found');
      }

      // Method 1: Try html2canvas first (best quality)
      try {
        const html2canvas = (window as any).html2canvas;
        if (html2canvas) {
          const canvas = await html2canvas(container, {
            backgroundColor: 'white',
            scale: scaleFactor,
            useCORS: true,
            allowTaint: true,
            width: baseWidth,
            height: baseHeight,
            logging: false,
            imageTimeout: 0,
            removeContainer: true,
          });
          const base64 = canvas.toDataURL('image/png', 1.0).split(',')[1];
          resolve(base64);
          return;
        }
      } catch (html2canvasError) {
        console.log('html2canvas failed, trying SVG method');
      }

      // Method 2: SVG to Canvas conversion with proper bounds calculation
      try {
        const svgClone = svgElement.cloneNode(true) as SVGElement;

        // Get the actual content bounds from the SVG
        let bbox: DOMRect | null = null;
        try {
          bbox = (svgElement as any).getBBox();
        } catch (e) {
          console.warn('Could not get SVG bounding box:', e);
        }
        const viewBox = svgElement.getAttribute('viewBox');

        let contentWidth = baseWidth;
        let contentHeight = baseHeight;

        if (viewBox) {
          const [_x, _y, w, h] = viewBox.split(' ').map(Number);
          contentWidth = w;
          contentHeight = h;
        } else if (bbox && bbox.width > 0 && bbox.height > 0) {
          // Add some padding around the content
          const padding = 50;
          contentWidth = bbox.width + padding * 2;
          contentHeight = bbox.height + padding * 2;
        }

        // Calculate final dimensions with scale factor
        const finalWidth = Math.round(contentWidth * scaleFactor);
        const finalHeight = Math.round(contentHeight * scaleFactor);

        // Set high resolution dimensions on the clone
        svgClone.setAttribute('width', finalWidth.toString());
        svgClone.setAttribute('height', finalHeight.toString());

        // Preserve the viewBox or set it based on content
        if (!viewBox && bbox && bbox.width > 0 && bbox.height > 0) {
          const padding = 50;
          svgClone.setAttribute(
            'viewBox',
            `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`
          );
        }

        svgClone.style.backgroundColor = 'white';
        svgClone.style.textRendering = 'geometricPrecision';
        svgClone.style.shapeRendering = 'geometricPrecision';

        // Enhance text rendering
        const textElements = svgClone.querySelectorAll('text');
        textElements.forEach((text) => {
          text.style.setProperty('font-smooth', 'always');
          text.style.setProperty('-webkit-font-smoothing', 'antialiased');
          text.style.textRendering = 'geometricPrecision';
        });

        // Serialize and convert
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svgClone);
        const svgBase64 = btoa(unescape(encodeURIComponent(svgString)));
        const svgDataUrl = `data:image/svg+xml;base64,${svgBase64}`;

        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = finalWidth;
          canvas.height = finalHeight;
          const ctx = canvas.getContext('2d');

          if (ctx) {
            // Enable high-quality rendering
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Fill background
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Draw the SVG at full resolution
            ctx.drawImage(img, 0, 0, finalWidth, finalHeight);

            const base64 = canvas.toDataURL('image/png', 1.0).split(',')[1];
            resolve(base64);
          } else {
            reject(new Error('Could not get canvas context'));
          }
        };

        img.onerror = (error) => {
          reject(new Error('Failed to load SVG image: ' + error));
        };

        img.crossOrigin = 'anonymous';
        img.src = svgDataUrl;
      } catch (svgError) {
        reject(new Error('SVG conversion failed: ' + svgError));
      }
    } catch (error) {
      reject(new Error('BPMN import failed: ' + error));
    } finally {
      // Cleanup
      setTimeout(() => {
        try {
          if (bpmnViewer) {
            bpmnViewer.destroy();
          }
          if (container && container.parentNode) {
            document.body.removeChild(container);
          }
        } catch (cleanupError) {
          console.warn('Cleanup error:', cleanupError);
        }
      }, 100);
    }
  });
};

// Alternative method that auto-crops whitespace
export const convertBpmnXmlToBase64AutoCrop = async (
  xml: string,
  scaleFactor: number = 2
): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    const baseWidth = 1400;
    const baseHeight = 800;

    const container = document.createElement('div');
    container.style.width = `${baseWidth}px`;
    container.style.height = `${baseHeight}px`;
    container.style.position = 'absolute';
    container.style.left = '-99999px';
    container.style.top = '-99999px';
    container.style.backgroundColor = 'white';
    document.body.appendChild(container);

    let bpmnViewer: any;

    try {
      bpmnViewer = new BpmnJS({
        container,
        keyboard: { bindTo: null },
        additionalModules: [],
      });

      await bpmnViewer.importXML(xml);
      const canvas = bpmnViewer.get('canvas');
      canvas.zoom('fit-viewport', 'auto');

      await new Promise((resolve) => setTimeout(resolve, 1000));

      const svgElement = container.querySelector('svg');
      if (!svgElement) {
        throw new Error('SVG element not found');
      }

      // Get all visible elements to calculate tight bounds
      const allElements = svgElement.querySelectorAll('*');
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      allElements.forEach((element) => {
        try {
          // Type guard to check if element has getBBox method
          if ('getBBox' in element && typeof element.getBBox === 'function') {
            const rect = (element as any).getBBox();
            if (rect.width > 0 && rect.height > 0) {
              minX = Math.min(minX, rect.x);
              minY = Math.min(minY, rect.y);
              maxX = Math.max(maxX, rect.x + rect.width);
              maxY = Math.max(maxY, rect.y + rect.height);
            }
          }
        } catch (e) {
          // Some elements might not have getBBox, skip them
        }
      });

      if (minX === Infinity) {
        // Fallback to original dimensions
        minX = 0;
        minY = 0;
        maxX = baseWidth;
        maxY = baseHeight;
      }

      // Add padding
      const padding = 20;
      const contentWidth = maxX - minX + padding * 2;
      const contentHeight = maxY - minY + padding * 2;

      // Create final canvas with scaled dimensions
      const finalWidth = Math.round(contentWidth * scaleFactor);
      const finalHeight = Math.round(contentHeight * scaleFactor);

      const svgClone = svgElement.cloneNode(true) as SVGElement;
      svgClone.setAttribute('width', finalWidth.toString());
      svgClone.setAttribute('height', finalHeight.toString());
      svgClone.setAttribute(
        'viewBox',
        `${minX - padding} ${minY - padding} ${contentWidth} ${contentHeight}`
      );
      svgClone.style.backgroundColor = 'white';

      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgClone);
      const svgBase64 = btoa(unescape(encodeURIComponent(svgString)));
      const svgDataUrl = `data:image/svg+xml;base64,${svgBase64}`;

      const img = new Image();
      img.onload = () => {
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = finalWidth;
        outputCanvas.height = finalHeight;
        const ctx = outputCanvas.getContext('2d');

        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
          ctx.drawImage(img, 0, 0, finalWidth, finalHeight);

          const base64 = outputCanvas.toDataURL('image/png', 1.0).split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Could not get canvas context'));
        }
      };

      img.onerror = (error) => reject(new Error('Failed to load SVG: ' + error));
      img.crossOrigin = 'anonymous';
      img.src = svgDataUrl;
    } catch (error) {
      reject(new Error('BPMN processing failed: ' + error));
    } finally {
      setTimeout(() => {
        try {
          if (bpmnViewer) bpmnViewer.destroy();
          if (container && container.parentNode) {
            document.body.removeChild(container);
          }
        } catch (cleanupError) {
          console.warn('Cleanup error:', cleanupError);
        }
      }, 100);
    }
  });
};

export const generatePdfWithAccurateTOC = async (
  docxTemplateUrl: string,
  docxData: Record<string, unknown>
): Promise<string | undefined> => {
  try {
    console.log('🔄 Step 1: Generating initial PDF with placeholder TOC...');

    // Step 1: Create initial PDF with placeholder TOC
    const initialPayload = {
      ...docxData,
      toc_page_1: 'XX',
      toc_page_2: 'XX',
      toc_page_3: 'XX',
      toc_page_4: 'XX',
      toc_page_5: 'XX',
    };

    const initialPdfBase64 = await generatePdfBased64(docxTemplateUrl, initialPayload);
    if (!initialPdfBase64) {
      throw new Error('Initial PDF generation failed');
    }
    const sectionPages = await analyzePDFForSections(initialPdfBase64);

    const finalPayload = {
      ...docxData,
      ...sectionPages,
    };

    const finalPdfBase64 = await generatePdfBased64(docxTemplateUrl, finalPayload);

    return finalPdfBase64;
  } catch (error) {
    console.error('Two-pass PDF generation failed:', error);

    const fallbackPayload = {
      ...docxData,
      toc_page_1: 4,
      toc_page_2: 5,
      toc_page_3: 7,
      toc_page_4: 9,
      toc_page_5: 11,
    };

    return await generatePdfBased64(docxTemplateUrl, fallbackPayload);
  }
};

const analyzePDFForSections = async (pdfBase64: string): Promise<Record<string, number>> => {
  try {
    // Convert base64 to array buffer
    const pdfData = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0));

    const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
    const numPages = pdf.numPages;

    console.log(`📄 Analyzing ${numPages} pages for section headers...`);

    // Default fallbacks
    const sectionPages: Record<string, number> = {
      toc_page_1: 4,
      toc_page_2: 5,
      toc_page_3: 7,
      toc_page_4: 9,
      toc_page_5: 11,
    };

    // Section patterns to look for
    const sectionPatterns = [
      { key: 'toc_page_1', pattern: /executive\s+summary/i, label: 'Executive Summary' },
      { key: 'toc_page_2', pattern: /strategic\s+context/i, label: 'Strategic Context' },
      { key: 'toc_page_3', pattern: /data\s+requirements/i, label: 'Data Requirements' },
      {
        key: 'toc_page_4',
        pattern: /suggested\s+agent\s+architecture/i,
        label: 'Agent Architecture',
      },
      {
        key: 'toc_page_5',
        pattern: /feasibility\s*(&|and|&amp;)\s*impact/i,
        label: 'Feasibility & Impact',
      },
    ];

    // Track found sections to avoid duplicates
    const foundSections = new Set<string>();

    const initialPdfPage = 4;

    // Extract text from each page
    for (let pageNum = initialPdfPage; pageNum <= numPages; pageNum++) {
      try {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();

        // Combine all text items into a single string
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ')
          .toLowerCase();

        //console.log(`📖 Page ${pageNum} text preview:`, pageText.substring(0, 100) + '...');

        // Check each section pattern
        for (const section of sectionPatterns) {
          if (!foundSections.has(section.key) && section.pattern.test(pageText)) {
            sectionPages[section.key] = pageNum;
            foundSections.add(section.key);
            console.log(`📍 Found ${section.label} on page ${pageNum}`);
          }
        }

        // If we've found all sections, we can break early
        if (foundSections.size === sectionPatterns.length) {
          console.log('✅ All sections found, stopping analysis');
          break;
        }
      } catch (pageError) {
        console.warn(`⚠️ Failed to analyze page ${pageNum}:`, pageError);
      }
    }

    return sectionPages;
  } catch (error) {
    console.error('PDF analysis failed:', error);

    // Return reasonable defaults
    return {
      toc_page_1: 4,
      toc_page_2: 5,
      toc_page_3: 7,
      toc_page_4: 9,
      toc_page_5: 11,
    };
  }
};

/**
 * Update hyperlink URLs in a Word document by modifying ONLY .rels files
 * This ensures document content and embedded data remain untouched
 */
export const updateWordDocLinks = async (
  wordBlob: Blob,
  linkReplacements: { oldUrl: string; newUrl: string }[]
): Promise<Blob> => {
  const arrayBuffer = await wordBlob.arrayBuffer();
  const zip = new PizZip(arrayBuffer);

  // Find all .rels files (these contain the actual URL mappings)
  const relsFiles = Object.keys(zip.files).filter(
    (filename) => filename.endsWith('.rels') && !zip.files[filename].dir
  );

  console.log('Found .rels files:', relsFiles);

  relsFiles.forEach((relsFile) => {
    try {
      const fileObj = zip.file(relsFile);
      if (!fileObj) return;

      // Read as text with proper encoding
      const content = fileObj.asText();
      if (!content) return;

      let updatedContent = content;
      let modified = false;

      // Replace URLs in the relationships XML
      linkReplacements.forEach(({ oldUrl, newUrl }) => {
        // Only process if the old URL exists in this file
        if (!updatedContent.includes(oldUrl)) {
          return;
        }

        // Escape special XML characters in the new URL
        const escapedNewUrl = newUrl
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&apos;');

        // Escape special regex characters in old URL for pattern matching
        const escapedOldUrlRegex = oldUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        // More precise regex: match Target attribute with the exact URL
        // This handles both double and single quotes
        const regexDouble = new RegExp(`(Target=")${escapedOldUrlRegex}(")`, 'g');
        const regexSingle = new RegExp(`(Target=')${escapedOldUrlRegex}(')`, 'g');

        // Track if replacement happened
        const beforeReplace = updatedContent;
        updatedContent = updatedContent.replace(regexDouble, `$1${escapedNewUrl}$2`);
        updatedContent = updatedContent.replace(regexSingle, `$1${escapedNewUrl}$2`);

        if (updatedContent !== beforeReplace) {
          modified = true;
          console.log(`✅ Replaced link in ${relsFile}:`);
          console.log(`   FROM: ${oldUrl}`);
          console.log(`   TO:   ${newUrl}`);
        }
      });

      // Only update the file if we actually modified it
      if (modified) {
        // Use the same encoding options as the original zip
        zip.file(relsFile, updatedContent);
      }
    } catch (error) {
      console.error(`❌ Error processing ${relsFile}:`, error);
    }
  });

  // Generate with the same options to preserve structure
  const outBuffer = zip.generate({
    type: 'arraybuffer',
    compression: 'DEFLATE',
    compressionOptions: {
      level: 6,
    },
  });

  return new Blob([outBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

export const generateWordWithAccurateTOC = async (
  docxTemplateUrl: string,
  docxData: Record<string, unknown>
): Promise<Blob | undefined> => {
  try {
    console.log('🔄 Step 1: Generating initial Word document with placeholder TOC...');

    // Step 1: Create initial Word document with placeholder TOC
    const initialPayload = {
      ...docxData,
      toc_page_1: 'XX',
      toc_page_2: 'XX',
      toc_page_3: 'XX',
      toc_page_4: 'XX',
      toc_page_5: 'XX',
    };

    const initialWordBlob = await generateWordBlob(docxTemplateUrl, initialPayload);

    // Step 2: Convert initial Word document to PDF for page analysis
    const formData = new FormData();
    formData.append('file', initialWordBlob, 'temp.docx');

    const pdfResponse = await httpService<{ pdf_base64: string }>(
      httpConstants.METHOD_TYPE.POST,
      `${API_BASE}/api/v1/word-to-pdf/`,
      formData
    );

    if (!pdfResponse?.pdf_base64) {
      throw new Error('Failed to convert Word to PDF for analysis');
    }

    // Step 3: Analyze the PDF to get accurate page numbers
    const sectionPages = await analyzePDFForSections(pdfResponse.pdf_base64);

    // Step 4: Generate final Word document with accurate page numbers
    const finalPayload = {
      ...docxData,
      ...sectionPages,
    };

    const finalWordBlob = await generateWordBlob(docxTemplateUrl, finalPayload);

    console.log('✅ Word document generated successfully with accurate TOC!');
    return finalWordBlob;
  } catch (error) {
    console.error('Two-step Word document generation failed:', error);

    // Fallback: Generate Word document with estimated page numbers
    console.log('🔄 Fallback: Using estimated page numbers...');
    const fallbackPayload = {
      ...docxData,
      toc_page_1: 4,
      toc_page_2: 5,
      toc_page_3: 7,
      toc_page_4: 9,
      toc_page_5: 11,
    };

    return await generateWordBlob(docxTemplateUrl, fallbackPayload);
  }
};
